// CEO Dashboard — one read-only page summarising every ERP module
// (backend/ceo_dashboard.py → GET /api/ceo-dashboard/summary). Each card links
// to the module that owns its numbers; the Alerts list shows the exceptions
// that need management's attention. The date range (month to date by default)
// drives the flow figures — revenue, intake, quotes, dispatches, POs, QC pass
// rate, OTD % and the trend; order book, WIP and open counts are always as of
// now. Money tiles are INR; other currencies are
// listed beside them, never added in.
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import {
  IndianRupee, ShoppingBag, BookOpen, Target, AlertTriangle, AlertOctagon, Truck, Factory,
  ShieldCheck, ShoppingCart, Boxes, PenTool, ChevronRight, RefreshCw, Briefcase, CheckCircle2, CalendarRange,
} from "lucide-react";
import ConnectedAppsSummary from "@/components/dashboard/ConnectedAppsSummary";

const BLUE = "#2563eb";
const AMBER = "#f59e0b";

const n = (v) => (v === null || v === undefined ? "—" : Number(v).toLocaleString("en-IN"));
const pct = (v) => (v === null || v === undefined ? "—" : `${v}%`);
const lakh = (v) => `₹${(Number(v) || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })} L`;
const toLakh = (inr) => lakh((Number(inr) || 0) / 1e5);
// Local calendar date as YYYY-MM-DD (not toISOString, which shifts to UTC).
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const fmtDay = (v) => new Date(`${v}T00:00:00`).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });

// Preset ranges. Indian financial year runs April–March.
export const PRESETS = [
  { key: "mtd", label: "This month", range: (t) => [new Date(t.getFullYear(), t.getMonth(), 1), t] },
  { key: "last_month", label: "Last month",
    range: (t) => [new Date(t.getFullYear(), t.getMonth() - 1, 1), new Date(t.getFullYear(), t.getMonth(), 0)] },
  { key: "qtd", label: "This quarter",
    range: (t) => [new Date(t.getFullYear(), Math.floor(t.getMonth() / 3) * 3, 1), t] },
  { key: "last_3m", label: "Last 3 months", range: (t) => [new Date(t.getFullYear(), t.getMonth() - 2, 1), t] },
  { key: "fytd", label: "FY to date",
    range: (t) => [new Date(t.getMonth() >= 3 ? t.getFullYear() : t.getFullYear() - 1, 3, 1), t] },
  { key: "last_12m", label: "Last 12 months", range: (t) => [new Date(t.getFullYear(), t.getMonth() - 11, 1), t] },
];
export const presetRange = (key, today = new Date()) => {
  const p = PRESETS.find((x) => x.key === key) || PRESETS[0];
  return p.range(today).map(ymd);
};

function RangeFilter({ preset, from, to, onPreset, onCustom }) {
  const [f, setF] = useState(from);
  const [t, setT] = useState(to);
  useEffect(() => { setF(from); setT(to); }, [from, to]);
  const invalid = !f || !t || f > t;
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-3 flex flex-wrap items-center gap-2" data-testid="ceo-range-filter">
      <CalendarRange className="w-4 h-4 text-slate-500" aria-hidden="true" />
      <span className="text-xs uppercase tracking-[0.12em] text-slate-500 mr-1">Date range</span>
      {PRESETS.map((p) => (
        <button key={p.key} type="button" onClick={() => onPreset(p.key)} data-testid={`ceo-preset-${p.key}`}
                aria-pressed={preset === p.key}
                className={`text-xs px-2.5 py-1 rounded-md border ${preset === p.key
                  ? "bg-blue-600 border-blue-600 text-white" : "border-slate-200 text-slate-700 hover:bg-slate-50"}`}>
          {p.label}
        </button>
      ))}
      <div className="flex items-center gap-1.5 ml-auto text-xs">
        <label className="sr-only" htmlFor="ceo-from">From</label>
        <input id="ceo-from" type="date" value={f} max={t || undefined} onChange={(e) => setF(e.target.value)}
               className="border border-slate-200 rounded-md px-2 py-1" data-testid="ceo-from" />
        <span className="text-slate-400">to</span>
        <label className="sr-only" htmlFor="ceo-to">To</label>
        <input id="ceo-to" type="date" value={t} min={f || undefined} onChange={(e) => setT(e.target.value)}
               className="border border-slate-200 rounded-md px-2 py-1" data-testid="ceo-to" />
        <button type="button" disabled={invalid || (f === from && t === to)} onClick={() => onCustom(f, t)}
                data-testid="ceo-apply-range"
                className="px-2.5 py-1 rounded-md border border-blue-600 text-blue-700 hover:bg-blue-50 disabled:opacity-40">
          Apply
        </button>
      </div>
    </div>
  );
}

const others = (o) => Object.entries(o || {}).map(([c, v]) => `+ ${c} ${Number(v).toLocaleString("en-IN")}`).join(" · ");

function Headline({ label, value, sub, icon: Icon, to, tid, tone = "text-blue-700 bg-blue-50" }) {
  return (
    <Link to={to} className="kpi-card block hover:border-blue-300 transition-colors" data-testid={tid}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-[0.14em] text-slate-500">{label}</div>
          <div className="mt-2 text-2xl font-extrabold text-slate-900 mono" style={{ fontFamily: "Manrope" }}>{value}</div>
          {sub && <div className="text-xs text-slate-500 mt-1 truncate">{sub}</div>}
        </div>
        <div className={`w-9 h-9 shrink-0 rounded-md flex items-center justify-center ${tone}`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
    </Link>
  );
}

function ModuleCard({ icon: Icon, title, to, rows, tid, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 flex flex-col" data-testid={tid}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-800" style={{ fontFamily: "Manrope" }}>
          <Icon className="w-4 h-4 text-blue-600" /> {title}
        </div>
        <Link to={to} className="text-xs text-blue-700 hover:underline flex items-center gap-0.5">
          Open <ChevronRight className="w-3 h-3" />
        </Link>
      </div>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
        {rows.filter(Boolean).map(([label, value, link, warn]) => (
          <div key={label} className="min-w-0">
            <dt className="text-[10px] uppercase tracking-[0.12em] text-slate-500 truncate">{label}</dt>
            <dd className={`text-lg font-bold mono ${warn ? "text-red-700" : "text-slate-900"}`}>
              {link ? <Link to={link} className="hover:underline">{value}</Link> : value}
            </dd>
          </div>
        ))}
      </dl>
      {children}
    </div>
  );
}

function Alerts({ alerts }) {
  if (!alerts.length) {
    return (
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-sm text-emerald-800 flex items-center gap-2"
           data-testid="ceo-no-alerts">
        <CheckCircle2 className="w-4 h-4" /> Nothing needs your attention right now.
      </div>
    );
  }
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="ceo-alerts">
      <h3 className="text-sm font-semibold text-slate-800 mb-3" style={{ fontFamily: "Manrope" }}>Needs attention</h3>
      <ul className="divide-y divide-slate-100">
        {alerts.map((a, i) => (
          <li key={i} className="py-2 flex items-center gap-3 text-sm" data-testid={`ceo-alert-${i}`}>
            {a.level === "red"
              ? <AlertOctagon className="w-4 h-4 text-red-600 shrink-0" aria-label="Critical" />
              : <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" aria-label="Warning" />}
            <span className={`text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded ${a.level === "red" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-800"}`}>
              {a.level === "red" ? "Critical" : "Warning"}
            </span>
            <span className="text-slate-500 w-24 shrink-0">{a.module}</span>
            <span className="text-slate-800 flex-1">{a.text}</span>
            <Link to={a.path} className="text-xs text-blue-700 hover:underline shrink-0">View</Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function TrendChart({ trend }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 lg:col-span-2" data-testid="ceo-trend">
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="text-sm font-semibold text-slate-800" style={{ fontFamily: "Manrope" }}>Revenue vs order intake</h3>
        <span className="text-[11px] text-slate-500 uppercase tracking-[0.12em]">₹ lakh per month · INR</span>
      </div>
      <div className="h-60">
        <ResponsiveContainer>
          <BarChart data={trend} barGap={2} margin={{ top: 4, right: 4, left: -12, bottom: 0 }}>
            <CartesianGrid stroke="#e2e8f0" strokeDasharray="4 4" vertical={false} />
            <XAxis dataKey="label" tick={{ fill: "#64748b", fontSize: 12 }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fill: "#64748b", fontSize: 12 }} axisLine={false} tickLine={false} />
            <Tooltip cursor={{ fill: "rgba(37,99,235,0.06)" }} formatter={(v) => lakh(v)} />
            <Legend wrapperStyle={{ fontSize: 12, color: "#334155" }} />
            <Bar dataKey="revenue_lakh" name="Invoiced revenue" fill={BLUE} radius={[4, 4, 0, 0]} maxBarSize={28} />
            <Bar dataKey="order_intake_lakh" name="Order intake" fill={AMBER} radius={[4, 4, 0, 0]} maxBarSize={28} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <table className="w-full mt-3 text-xs" data-testid="ceo-trend-table">
        <thead>
          <tr className="text-slate-500">
            <th className="text-left font-medium py-1">Month</th>
            {trend.map((m) => <th key={m.month} className="text-right font-medium">{m.label}</th>)}
          </tr>
        </thead>
        <tbody className="mono text-slate-700">
          <tr><td className="py-1 text-slate-500">Revenue (L)</td>{trend.map((m) => <td key={m.month} className="text-right">{n(m.revenue_lakh)}</td>)}</tr>
          <tr><td className="py-1 text-slate-500">Intake (L)</td>{trend.map((m) => <td key={m.month} className="text-right">{n(m.order_intake_lakh)}</td>)}</tr>
        </tbody>
      </table>
    </div>
  );
}

function StageStrip({ stages }) {
  const max = Math.max(1, ...stages.map((s) => s.jobs));
  return (
    <div className="mt-4 space-y-1.5" data-testid="ceo-stage-strip">
      {stages.map((s) => (
        <div key={s.key} className="flex items-center gap-2 text-xs" title={`${s.label}: ${s.jobs} job(s), ${s.qty} qty`}>
          <span className="w-28 text-slate-500 truncate">{s.label}</span>
          <div className="flex-1 h-2.5 bg-slate-100 rounded">
            <div className="h-2.5 rounded" style={{ width: `${(s.jobs / max) * 100}%`, background: BLUE, minWidth: s.jobs ? 4 : 0 }} />
          </div>
          <span className="w-8 text-right mono text-slate-700">{s.jobs}</span>
        </div>
      ))}
    </div>
  );
}

function LeadFunnel({ funnel }) {
  const max = Math.max(1, ...funnel.map((f) => f.count));
  return (
    <div className="mt-4 space-y-1.5" data-testid="ceo-lead-funnel">
      {funnel.map((f) => (
        <div key={f.stage} className="flex items-center gap-2 text-xs" title={`${f.stage}: ${f.count}`}>
          <span className="w-24 text-slate-500 capitalize">{f.stage}</span>
          <div className="flex-1 h-2.5 bg-slate-100 rounded">
            <div className="h-2.5 rounded" style={{ width: `${(f.count / max) * 100}%`, background: BLUE, minWidth: f.count ? 4 : 0 }} />
          </div>
          <span className="w-8 text-right mono text-slate-700">{f.count}</span>
        </div>
      ))}
    </div>
  );
}

export default function CeoDashboard() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [preset, setPreset] = useState("mtd");
  const [range, setRange] = useState(() => presetRange("mtd"));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/ceo-dashboard/summary", { params: { from: range[0], to: range[1] } });
      setData(r.data);
      setError("");
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't load the CEO Dashboard.");
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => { load(); }, [load]);

  const choosePreset = (key) => { setPreset(key); setRange(presetRange(key)); };
  const chooseCustom = (f, t) => { setPreset("custom"); setRange([f, t]); };

  if (error && !data) return <div className="text-sm text-red-700" data-testid="ceo-error">{error}</div>;
  if (!data) return <div className="text-slate-500 text-sm" data-testid="ceo-loading">Loading CEO Dashboard...</div>;

  const { headline: h, sales: s, dispatch: d, production: p, quality: q, purchase: pu, inventory: inv, design: de, otd } = data;
  const asOf = new Date(data.as_of).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  const per = data.period ? `${fmtDay(data.period.from)} – ${fmtDay(data.period.to)}` : "";

  return (
    <div className="space-y-6" data-testid="ceo-dashboard-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Management overview · all modules</div>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>
            CEO Dashboard
          </h1>
        </div>
        <div className="flex items-center gap-3 text-xs text-slate-500">
          <span>As of {asOf}</span>
          <button type="button" onClick={load} disabled={loading} data-testid="ceo-refresh"
                  className="inline-flex items-center gap-1 border border-slate-200 rounded-md px-2.5 py-1.5 hover:bg-slate-50 disabled:opacity-50">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </div>

      <RangeFilter preset={preset} from={range[0]} to={range[1]} onPreset={choosePreset} onCustom={chooseCustom} />
      {error && <div className="text-sm text-red-700" data-testid="ceo-range-error">{error}</div>}
      <div className="text-xs text-slate-500 -mt-3" data-testid="ceo-period-note">
        Revenue, intake, quotes, dispatches, POs, QC pass rate and OTD % cover <span className="font-semibold text-slate-700">{per}</span>.
        Order book, WIP, overdue and open counts are as of now.
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        <Headline label="Revenue" value={lakh(h.revenue_period_lakh)} icon={IndianRupee} to="/tax-invoices" tid="ceo-kpi-revenue"
                  sub={others(h.revenue_period.other) || `${n(h.invoices_period)} invoice(s)`} />
        <Headline label="Order intake" value={lakh(h.order_intake_period_lakh)} icon={ShoppingBag} to="/customer-sales-orders"
                  tid="ceo-kpi-intake" sub={others(h.order_intake_period.other) || `${n(s.sales_orders_period)} sales order(s)`} />
        <Headline label="Open order book (now)" value={lakh(h.order_book_lakh)} icon={BookOpen} to="/reports/pending-sales-orders"
                  tid="ceo-kpi-orderbook" sub={others(h.order_book.other) || `${n(h.open_orders)} order(s) to deliver`} />
        <Headline label="OTD %" value={pct(h.otd_period_pct)} icon={Target} to="/otd-board" tid="ceo-kpi-otd"
                  sub={`${n(otd.otd_period.on_time)} of ${n(otd.otd_period.due)} on time`}
                  tone={h.otd_period_pct !== null && h.otd_period_pct < 90 ? "text-red-700 bg-red-50" : "text-emerald-700 bg-emerald-50"} />
        <Headline label="Overdue orders (now)" value={n(h.overdue_orders)} icon={AlertTriangle} to="/otd-board" tid="ceo-kpi-overdue"
                  sub={`Past delivery date · ${n(otd.amber_orders)} due today/tomorrow · ${n(otd.green_orders)} on track`}
                  tone={h.overdue_orders ? "text-red-700 bg-red-50" : "text-emerald-700 bg-emerald-50"} />
        <Headline label="Value at risk (now)" value={lakh(h.value_at_risk_lakh)} icon={AlertOctagon} to="/otd-board" tid="ceo-kpi-risk"
                  sub="Overdue order value" tone="text-amber-700 bg-amber-50" />
      </div>

      <Alerts alerts={data.alerts || []} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <TrendChart trend={data.trend || []} />
        <ModuleCard icon={Briefcase} title="Sales & CRM" to="/crm" tid="ceo-card-sales" rows={[
          ["Quotes in range", n(s.quotations_period), "/quotations"],
          ["Open quote value", toLakh(s.quotations_open_value.inr), "/quotations"],
          ["Quote win rate", pct(s.quote_win_rate_pct)],
          ["Lead win rate", pct(s.lead_win_rate_pct)],
          ["Open leads", n(s.leads_open), "/crm"],
          ["New IndiaMART leads", n(s.indiamart_new), "/crm/indiamart"],
        ]}>
          <LeadFunnel funnel={s.lead_funnel} />
        </ModuleCard>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        <ModuleCard icon={Factory} title="Production" to="/production-flow" tid="ceo-card-production" rows={[
          ["Active jobs (WIP)", n(p.active_jobs), "/production-flow"],
          ["WIP qty", n(p.wip_qty)],
          ["Finished goods qty", n(p.finished_qty), "/production-flow/finished"],
          ["Jobs awaiting QC", n(p.jobs_awaiting_qc), "/quality-inspections"],
          ["Open work orders", n(p.work_orders_open), "/work-orders"],
          ["Awaiting prod. approval", n(p.awaiting_production_approval), "/production-approvals"],
        ]}>
          <StageStrip stages={p.stages} />
        </ModuleCard>

        <ModuleCard icon={Truck} title="Dispatch & Billing" to="/delivery-challans" tid="ceo-card-dispatch" rows={[
          ["DCs ready to dispatch", n(d.dcs_ready), "/delivery-challans"],
          ["Dispatched in range", n(d.dispatched_period), "/delivery-challans"],
          ["Dispatched, not invoiced", n(d.invoices_pending), "/tax-invoices", d.invoices_pending > 0],
          ["Draft invoices", n(d.invoices_draft), "/tax-invoices"],
          ["Open packing slips", n(d.packing_slips_open), "/packing-slips"],
          ["Open transfer orders", n(p.transfer_orders_open), "/sales-orders"],
          ["Open sales orders", n(s.sales_orders_open), "/customer-sales-orders"],
          ["Proforma invoices", n(s.proforma_invoices), "/proforma-invoices"],
        ]} />

        <ModuleCard icon={ShieldCheck} title="Quality" to="/quality" tid="ceo-card-quality" rows={[
          ["QC pass rate (range)", pct(q.pass_rate_period_pct), "/quality-inspections"],
          ["Inspections pending", n(q.inspections_pending), "/quality-inspections"],
          ["Production NCRs open", n(q.production_ncrs_open), "/quality/production-ncrs", q.production_ncrs_open > 0],
          ["Incoming NCRs open", n(q.incoming_ncrs_open), "/quality/incoming-inspection/ncr-register"],
          ["Rework orders open", `${n(q.rework_orders_open)}${q.rework_overdue ? ` (${q.rework_overdue} late)` : ""}`,
            "/quality/rework-orders", q.rework_overdue > 0],
          ["Calibration overdue", `${n(q.calibration_overdue)} / ${n(q.instruments)}`, "/quality/calibration", q.calibration_overdue > 0],
          ["Calibration due ≤15 d", n(q.calibration_due_soon), "/quality/calibration"],
        ]} />

        <ModuleCard icon={ShoppingCart} title="Purchase" to="/purchase-orders" tid="ceo-card-purchase" rows={[
          ["Open POs", n(pu.pos_open), "/purchase-orders"],
          ["Open PO value", toLakh(pu.pos_open_value.inr), "/purchase-orders"],
          ["POs raised in range", n(pu.pos_period), "/purchase-orders"],
          ["PO value in range", toLakh(pu.pos_period_value.inr)],
          ["PRs awaiting approval", n(pu.prs_pending_approval), "/purchase-requests"],
          ["PRs approved, not ordered", n(pu.prs_approved_not_ordered), "/purchase-requests"],
          ["GINs awaiting QC", n(pu.gins_pending_qc), "/reports/pending-qc"],
          ["Active suppliers", n(pu.active_suppliers), "/supplier-master"],
        ]} />

        <ModuleCard icon={Boxes} title="Inventory & Stores" to="/production-flow/inventory" tid="ceo-card-inventory" rows={[
          ["Stock value (at cost)", lakh(inv.stock_value_lakh), "/production-flow/inventory"],
          ["Items", n(inv.skus), "/items"],
          ["Low-stock SKUs", n(inv.low_stock), "/stock-availability", inv.low_stock > 0],
          ["Open indents", n(inv.indents_open), "/indents"],
        ]} />

        <ModuleCard icon={PenTool} title="Design & OTD Board" to="/design" tid="ceo-card-design" rows={[
          ["Open design requests", n(de.requests_open), "/design/requests"],
          ["Overdue requests", n(de.requests_overdue), "/design/requests", de.requests_overdue > 0],
          ["Open ECRs", n(de.ecrs_open), "/design/changes"],
          ["Open ECNs", n(de.ecns_open), "/design/changes"],
          ["OTD actions open", n(otd.actions_open), "/otd-board"],
          ["Actions blocked", n(otd.blocked_actions), "/otd-board", otd.blocked_actions > 0],
          ["Actions overdue", n(otd.actions_overdue), "/otd-board", otd.actions_overdue > 0],
          ["OTD % (last 7 d)", pct(otd.otd_7d_pct), "/otd-board"],
        ]} />
      </div>

      {/* HR and Sales Reporter figures come from the connected apps, using the viewer's own sign-in. */}
      <ConnectedAppsSummary />
    </div>
  );
}
